import { Dialog } from '@cimplico/components';
import { toastNotification } from '../../components/toast/Toast';
import { useUpdateWorksheets } from '../../queries/use-update-worksheets';
import { WorksheetTypeEnum } from '../../types/worksheet';
import { formatMixedCase } from '../../utilities/formatting';
import plural from '../../utilities/plural';

type WorksheetTypeChangeConfirmProps = {
    worksheetIds: string[];
    newType: WorksheetTypeEnum;
    onClose: () => void;
    onUpdate: () => void;
    onError?: (errorMessages: { [id: string]: string }) => void;
};

function WorksheetTypeChangeConfirm({ worksheetIds, newType, onClose, onUpdate, onError }: WorksheetTypeChangeConfirmProps): JSX.Element {
    const updateWorksheetsQuery = useUpdateWorksheets();

    const _newType = formatMixedCase(newType);
    const description = `Do you want to change ${plural(
        worksheetIds.length,
        "worksheet's",
        `${worksheetIds.length} worksheets'`
    )} type to ${_newType}? Entries and ${plural(worksheetIds.length, 'status', 'statuses')} might be affected.`;

    function handleTypeChange(): void {
        updateWorksheetsQuery.mutate(
            worksheetIds.map((id) => ({ id, fields: { type: newType } })),
            {
                onSuccess: () => {
                    onUpdate();
                    toastNotification(
                        'success',
                        `${worksheetIds.length} ${plural(worksheetIds.length, 'worksheet', 'worksheets')} updated to ${_newType} type!`
                    );
                },
                onError: (err) => {
                    const results = err.response?.data?.results ?? [];

                    if (newType.length === 1) {
                        toastNotification(
                            'error',
                            `Unable to change this worksheet to ${_newType} type!`,
                            results[0].error?.fields?.errorMessage
                        );
                        onClose();
                        return;
                    }

                    const errors = results.filter((r: { statusCode: number }) => r.statusCode >= 400);
                    const success = results.filter((r: { statusCode: number }) => r.statusCode < 400);

                    if (success.length > 0) {
                        toastNotification(
                            'success',
                            `${success.length} ${plural(success.length, 'worksheet', 'worksheets')} updated to ${_newType} type!`
                        );
                    }

                    toastNotification(
                        'error',
                        `Unable to change ${errors.length} ${plural(errors.length, 'worksheet', 'worksheets')} to ${_newType} type!`
                    );

                    const errorMessages: { [id: string]: string } = {};

                    for (const err of errors) {
                        errorMessages[err.error.fields.id] = err.error.fields.errorMessage;
                    }

                    onError && onError(errorMessages);
                    onClose();
                },
            }
        );
    }

    return (
        <Dialog
            title={`Change ${plural(worksheetIds.length, "Worksheet's", "Worksheets'")} Type`}
            type="error"
            description={description}
            isOpen
            primaryAction="Confirm"
            secondaryAction="Cancel"
            onPrimaryActionClick={handleTypeChange}
            onClose={onClose}
            onSecondaryActionClick={onClose}
            isLoading={updateWorksheetsQuery.isLoading}
        />
    );
}
export default WorksheetTypeChangeConfirm;
